import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { loadFullDictionary } from '../../shared/dictionary';
import type { GauntletAware } from '../../shared/gauntletStep';
import {
  adjacentLetters,
  applyBend,
  applySlide,
  applySwap,
  bendCount,
  billableMoveCount,
  computeHint,
  findAnagrams,
  freeMoveBudget,
  type HintHighlight,
  type PivotMove,
  type PivotPuzzle,
} from './types';
import './pivot.css';

const ALPHABET = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'];

/**
 * Pivot play surface. Gauntlet-only today — the host owns lives, hints and the
 * timer. Billable moves past `par + 2` each cost a life.
 */
export default function PivotPlay({
  puzzle,
  gauntlet,
  onSolved,
}: GauntletAware & {
  puzzle: PivotPuzzle;
  onSolved?: () => void;
}) {
  const [currentWord, setCurrentWord] = useState(puzzle.startWord);
  const [moves, setMoves] = useState<PivotMove[]>([]);
  const [selectedPos, setSelectedPos] = useState<number | null>(null);
  const [showSlide, setShowSlide] = useState(false);
  const [hint, setHint] = useState<HintHighlight | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dict, setDict] = useState<Set<string> | null>(null);

  const solvedRef = useRef(false);
  useEffect(() => {
    loadFullDictionary().then(setDict);
  }, []);

  const isValidWord = useCallback((w: string) => dict?.has(w.toUpperCase()) ?? false, [dict]);
  const isComplete = currentWord === puzzle.targetWord;

  useEffect(() => {
    if (isComplete && !solvedRef.current) {
      solvedRef.current = true;
      gauntlet?.onSolved();
      onSolved?.();
    }
  }, [isComplete, gauntlet, onSolved]);

  // Host-granted hint: highlight the position to aim at, and the letter.
  const lastHintSignal = useRef(gauntlet?.hintSignal ?? 0);
  useEffect(() => {
    const signal = gauntlet?.hintSignal ?? 0;
    if (signal > lastHintSignal.current) {
      lastHintSignal.current = signal;
      const h = computeHint(puzzle, currentWord, moves);
      if (h) {
        setHint(h);
        setSelectedPos(h.position);
        setNotice(`Aim for ${h.targetLetter} at position ${h.position + 1}.`);
      }
    }
  }, [gauntlet?.hintSignal, puzzle, currentWord, moves]);

  const budget = freeMoveBudget(puzzle);
  const billable = billableMoveCount(moves);
  const bends = bendCount(moves);
  const freeRemaining = Math.max(0, budget - billable);

  /**
   * Commit a move. A billable move made with the budget already exhausted
   * costs a life — bends are always free.
   */
  const commit = useCallback(
    (move: PivotMove) => {
      const overBudget = move.operation !== 'bend' && billable >= budget;
      setCurrentWord(move.toWord);
      setMoves((prev) => [...prev, move]);
      setSelectedPos(null);
      setShowSlide(false);
      setHint(null);
      setNotice(null);
      if (overBudget) gauntlet?.onWrong();
    },
    [billable, budget, gauntlet],
  );

  const handleResult = useCallback(
    (result: ReturnType<typeof applySwap>) => {
      switch (result.kind) {
        case 'success':
          commit(result.move);
          break;
        case 'invalidWord':
          setNotice(`${result.word} isn't a word.`);
          break;
        case 'invalidOperation':
          setNotice(result.reason);
          break;
        case 'sameAsCurrent':
          setNotice('That letter is already there.');
          break;
      }
    },
    [commit],
  );

  const doSwap = useCallback(
    (letter: string) => {
      if (selectedPos == null || isComplete) return;
      handleResult(applySwap(currentWord, selectedPos, letter, isValidWord));
    },
    [selectedPos, isComplete, currentWord, isValidWord, handleResult],
  );

  const doBend = useCallback(
    (letter: string) => {
      if (selectedPos == null || isComplete) return;
      handleResult(applyBend(currentWord, selectedPos, letter, isValidWord));
    },
    [selectedPos, isComplete, currentWord, isValidWord, handleResult],
  );

  const doSlide = useCallback(
    (anagram: string) => {
      if (isComplete) return;
      handleResult(applySlide(currentWord, anagram, isValidWord));
    },
    [isComplete, currentWord, isValidWord, handleResult],
  );

  const undo = useCallback(() => {
    if (!moves.length || isComplete) return;
    const last = moves[moves.length - 1];
    setCurrentWord(last.fromWord);
    setMoves((prev) => prev.slice(0, -1));
    setSelectedPos(null);
    setShowSlide(false);
    setHint(null);
    setNotice(null);
  }, [moves, isComplete]);

  const anagrams = useMemo(
    () => (dict && showSlide ? findAnagrams(currentWord, dict) : []),
    [dict, showSlide, currentWord],
  );

  const bendOptions = selectedPos == null ? [] : adjacentLetters(currentWord[selectedPos]);

  return (
    <div className="pv-board">
      <div className="pv-goal">
        <div className="pv-goal-col">
          <span className="pv-goal-label">START</span>
          <span className="pv-goal-word">{puzzle.startWord}</span>
        </div>
        <span className="pv-goal-arrow">→</span>
        <div className="pv-goal-col">
          <span className="pv-goal-label">TARGET</span>
          <span className="pv-goal-word is-target">{puzzle.targetWord}</span>
        </div>
      </div>

      <div className="pv-stats">
        <span className="pv-stat">
          PAR <strong>{puzzle.par}</strong>
        </span>
        <span className="pv-stat">
          MOVES <strong>{billable}</strong>
        </span>
        <span className={'pv-stat' + (freeRemaining === 0 ? ' is-warn' : '')}>
          BUDGET <strong>{freeRemaining}</strong>
        </span>
        {bends > 0 && (
          <span className="pv-stat is-bend">
            BENDS <strong>{bends}</strong>
          </span>
        )}
      </div>

      {/* Current word — tap a letter to act on that position. */}
      {/* Letter size derives from the word length so a 7-letter puzzle still
          fits a 320px screen — see --pv-letter in pivot.css. */}
      <div
        className="pv-word"
        style={{ ['--pv-len' as string]: String(currentWord.length) }}
      >
        {[...currentWord].map((ch, i) => (
          <button
            key={i}
            type="button"
            className={
              'pv-letter' +
              (i === selectedPos ? ' is-selected' : '') +
              (hint?.position === i ? ' is-hinted' : '') +
              (ch === puzzle.targetWord[i] ? ' is-placed' : '')
            }
            onClick={() => {
              setSelectedPos(selectedPos === i ? null : i);
              setShowSlide(false);
              setNotice(null);
            }}
            disabled={isComplete}
          >
            {ch}
          </button>
        ))}
      </div>

      {notice && <div className="pv-notice">{notice}</div>}

      {!isComplete && selectedPos != null && (
        <div className="pv-picker">
          <div className="pv-picker-section">
            <div className="pv-picker-title">
              BEND — keyboard neighbours, free
            </div>
            <div className="pv-picker-keys">
              {bendOptions.map((letter) => (
                <button
                  key={letter}
                  type="button"
                  className={
                    'pv-key is-bend' +
                    (hint?.targetLetter === letter ? ' is-hinted' : '')
                  }
                  onClick={() => doBend(letter)}
                >
                  {letter}
                </button>
              ))}
            </div>
          </div>
          <div className="pv-picker-section">
            <div className="pv-picker-title">SWAP — any letter, costs a move</div>
            <div className="pv-picker-keys">
              {ALPHABET.map((letter) => (
                <button
                  key={letter}
                  type="button"
                  className={
                    'pv-key' + (hint?.targetLetter === letter ? ' is-hinted' : '')
                  }
                  onClick={() => doSwap(letter)}
                >
                  {letter}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {!isComplete && showSlide && (
        <div className="pv-picker">
          <div className="pv-picker-title">SLIDE — rearrange into an anagram</div>
          {anagrams.length ? (
            <div className="pv-anagrams">
              {anagrams.map((word) => (
                <button
                  key={word}
                  type="button"
                  className="pv-anagram"
                  onClick={() => doSlide(word)}
                >
                  {word}
                </button>
              ))}
            </div>
          ) : (
            <div className="pv-empty">No anagrams available for {currentWord}.</div>
          )}
        </div>
      )}

      {!isComplete && (
        <div className="pv-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setShowSlide((s) => !s);
              setSelectedPos(null);
              setNotice(null);
            }}
          >
            {showSlide ? 'Close slide' : 'Slide'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={undo}
            disabled={!moves.length}
          >
            Undo
          </button>
        </div>
      )}

      {moves.length > 0 && (
        <div className="pv-chain">
          <span className="pv-chain-word">{puzzle.startWord}</span>
          {moves.map((m, i) => (
            <span key={i} className="pv-chain-step">
              <span className={`pv-chain-op is-${m.operation}`}>{m.operation}</span>
              <span className="pv-chain-word">{m.toWord}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
